import { useState, useEffect } from 'react';
import axios, { type AxiosInstance } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/hooks';

interface Params {
  filter_by: {
    date: {
      from: string;
      to: string;
    };
  };
  token: string;
  id: string;
}

function getQueryParams(url: string): Params {
  const params = new URLSearchParams(new URL(url).search);
  const result: Partial<Params> = {};

  params.forEach((value, key) => {
    const keys = key
      .replace(/\]\[/g, '/')
      .replace(/^\[|\]$/g, '')
      .split('/');
    keys.reduce((acc: any, part: string, index: number) => {
      if (index === keys.length - 1) {
        acc[part] = value;
      } else {
        acc[part] = acc[part] || {};
      }
      return acc[part];
    }, result);
  });

  return result as Params;
}

export const useGenerateReportsApi = () => {
  const { setAuth } = useAuth();

  const apiHttp: AxiosInstance = axios.create({
    baseURL: '/',
    withCredentials: true,
  });

  const params = getQueryParams(window.location.href);
  const [accessToken, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportData, setReportData] = useState<any>(null);

  useEffect(() => {
    if (accessToken) return;

    // Local-dev fallback: the PDF flow normally arrives with a backend-minted
    // one-time `token`. When opening /reports/generate directly in the browser
    // (no token) during local development, reuse the existing logged-in session
    // so the print page can be previewed locally. Never applies in production.
    if (!params.token) {
      if (import.meta.env.DEV) {
        const existing = localStorage.getItem('apiv1Auth');
        if (existing) {
          setToken(existing);
          return;
        }
      }
      setLoading(false);
      return;
    }

    const handleTokenExchange = async () => {
      const baseurl = window.location.hostname;
      const url = `https://${baseurl}/api/v1/auth/exchange`;
      const data = { token: params.token };

      try {
        const response = await axios.post(url, data);
        const token = response?.data?.data?.access_token;
        setToken(token);
        //todo: might need this when using in app view
        setAuth(token);
        localStorage.setItem('apiv1Auth', token);
      } catch (error) {
        console.error('Error during token exchange:', error);
      } finally {
        setLoading(false);
      }
    };

    handleTokenExchange();
  }, [params.token, setAuth, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    // Set up interceptor only once
    const interceptorId = apiHttp.interceptors.request.use(
      (config) => {
        const token = accessToken || '';
        const contentType =
          config?.data instanceof FormData
            ? 'multipart/form-data'
            : 'application/json';
        config.headers.set('Authorization', `Bearer ${token}`);
        config.headers.set('X-App', 'web');
        config.headers.set('X-Request-ID', uuidv4());
        config.headers.set('Content-Type', contentType);
        return config;
      },
      (error) => Promise.reject(error)
    );

    const fetchReportData = async () => {
      setLoading(true);
      try {
        const response = await apiHttp.get(
          `/api_ats/v1/reports/saved_reports/${params?.id || 1}`
        );
        setReportData(response?.data?.data);
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();

    return () => {
      // Cleanup: remove interceptor when effect re-runs or component unmounts
      apiHttp.interceptors.request.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return {
    apiHttp,
    accessToken,
    loading,
    params,
    reportData,
  };
};
